<template>
  <v-data-table
    :headers="headers"
    :items="items"
    :items-per-page="value.itemsPerPage"
    item-key="number"
    :mobile-breakpoint="0"
    :expanded.sync="expanded"
    :sort-by="value.sortBy"
    :sort-desc="value.sortDesc"
    :page="value.page"
    :loading="filterLoading"
    class="dashboard-table"
    :hide-default-header="$vuetify.breakpoint.mdAndDown"
    :footer-props="{ itemsPerPageText: 'Per page:' }"
    @update:page="updateModel({ page: $event })"
    @update:sort-by="updateModel({ sortBy: $event })"
    @update:sort-desc="updateModel({ sortDesc: $event })"
    @update:items-per-page="updateModel({ itemsPerPage: $event })"
  >
    <template #item="{ item, isExpanded, expand, index }">
      <OpenBalancesMobileCard
        v-if="$vuetify.breakpoint.mdAndDown"
        :item="item"
        :active-account-id="activeAccountId"
        :class="index === 0 ? 'mt-0' : 'mt-2'"
      />
      <tr
        v-else
        :class="`${isExpanded ? 'expanded' : ''}`"
        class="sc-broker-ranking-broker sc-open-balances-row"
        @click="expand(!isExpanded)"
      >
        <td class="sc-data-list-select" style="width: 80px">
          <v-list color="transparent">
            <v-list-item class="ml-0 pl-0 pr-0">
              <CommonChip class="sc-nbr">{{ item.number }}</CommonChip>
            </v-list-item>
          </v-list>
        </td>
        <td class="sc-data-list-subject">
          <router-link
            class="link-to-project"
            style="display: inline;"
            :to="getAccountRoute({ name: 'deal', params: { id: item._key }})"
          >
            {{ item.name }}
          </router-link>
          <br>
          <v-chip
            v-if="item.status === 'pending'"
            small
            dark
            class="info overline mt-2"
            :to="getAccountRoute({ name: 'deal', params: { id: item._key }})"
          >
            Pending invoice approval
          </v-chip>
        </td>
        <td class="sc-data-list">
          {{ item.transactionDate | formatDate }}
        </td>
        <td class="sc-data-list">
          {{ item.expected | formatCurrency }}
        </td>
        <td class="sc-data-list">
          {{ item.payments | formatCurrency }}
        </td>
        <td class="sc-data-list">
          {{ item.remaining | formatCurrency }}
        </td>
        <td class="sc-data-list">
          {{ item.inHouseExpected | formatCurrency }}
        </td>
        <td class="sc-data-list">
          {{ item.brokerGross | formatCurrency }}
        </td>
        <td class="sc-data-list">
          {{ item.brokerGrossRemaining | formatCurrency }}
        </td>
        <td style="width: 24px">
          <v-btn
            :loading="exportLoadingKey === item._key"
            :disabled="exportLoadingKey && exportLoadingKey !== item._key"
            icon
            small
            data-track="report.open-balances.export"
            @click.stop="$emit('click:export-single', item._key)"
          >
            <v-icon color="primary">mdi-file-export</v-icon>
          </v-btn>
        </td>
      </tr>
    </template>
    <template #expanded-item="{ headers, item }">
      <tr class="broker-ranking-detail">
        <td :colspan="headers.length">
          <v-card outlined class="ma-2">
            <OpenBalancesInvoiceTable :items-entry="item.invoices" />
          </v-card>
        </td>
      </tr>
    </template>
    <template #body.append>
      <tr v-if="!$vuetify.breakpoint.mdAndDown" class="grey lighten-2 tfoot">
        <td><v-chip dense>{{ totals.count }}</v-chip></td>
        <td />
        <td />
        <td>{{ totals.expected | formatCurrency }} </td>
        <td>{{ totals.paid | formatCurrency }}</td>
        <td>{{ totals.remaining | formatCurrency }}</td>
        <td>{{ totals.inHouseExpected | formatCurrency }} </td>
        <td>{{ totals.brokerGross | formatCurrency }}</td>
        <td>{{ totals.brokerGrossRemaining | formatCurrency }}</td>
        <td>
          <v-menu bottom left style="width: 300px;">
            <template #activator="{ on }">
              <v-btn icon v-on="on">
                <v-icon color="primary">mdi-file-export</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item :loading="isExportAllLoading" data-track="report.open-balances-summary.export" @click="$emit('click:export-all')">
                <v-list-item-content>
                  Open Balances
                </v-list-item-content>
              </v-list-item>
              <v-list-item :loading="isExportAllLoading" data-track="report.open-balances-detailed.export" @click="$emit('click:export-all-detailed')">
                <v-list-item-content class="no-wrap">
                  Detailed Open Balances
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </td>
      </tr>
    </template>
  </v-data-table>
</template>

<script lang="ts">
import { PropType } from 'vue';
import mixins from 'vue-typed-mixins';
import { capitalize } from 'lodash-es';

import { IHeader, ITableOptions } from '@/bundles/BaseTable/interfaces';
import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { IOpenBalanceListItem } from '@/bundles/Deal/types';
import { Nullable } from '@/utils/types';
import i18n from '@/bundles/Localization';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';

import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';
import OpenBalancesMobileCard from '@/bundles/Dashboard/components/OpenBalancesMobileCard.vue';
import CriticalDateMobileCard from '@/bundles/CriticalDates/components/CriticalDateMobileCard.vue';

import OpenBalancesInvoiceTable from './OpenBalancesInvoiceTable.vue';

interface IBrokerProductionTableData {
  headers: IHeader[];
  expanded: any[];
}

interface IBrokerProductionTableFooterTotals {
  count: number;
  expected: number;
  inHouseExpected: number;
  paid: number;
  brokerGross: number;
  remaining: number;
  brokerGrossRemaining: number;
}

const headers = [
  { text: '#', value: '#', sortable: false },
  {
    text: 'Name',
    value: 'name',
    sortable: true,
    class: '',
    icon: 'string',
  },
  {
    text: 'Transaction Date',
    value: 'transactionDate',
    sortable: true,
    class: '',
    icon: 'string',
  },
  {
    text: 'Total Commission',
    value: 'expected',
    sortable: true,
    class: '',
    icon: 'string',
  },
  {
    text: 'Total Paid',
    value: 'payments',
    sortable: true,
    class: '',
    icon: 'string',
  },
  {
    text: 'Total Outstanding',
    value: 'remaining',
    sortable: true,
    icon: 'date',
  },
  {
    text: 'Total In-House Commission',
    value: 'inHouseExpected',
    sortable: true,
    class: '',
    icon: 'string',
  },
  {
    text: `${capitalize(i18n.tc(LocalizationEnum.broker))} Gross`,
    value: 'brokerGross',
    sortable: true,
    class: '',
    icon: 'string',
  },
  {
    text: `${capitalize(i18n.tc(LocalizationEnum.broker))} Gross Outstanding`,
    value: 'brokerGrossRemaining',
    sortable: true,
    icon: 'date',
  },
  { text: 'Export', value: 'export', sortable: false },
];

export default mixins(AccountLayoutMixin).extend({
  name: 'OpenBalancesTable',

  components: {
    CriticalDateMobileCard,
    OpenBalancesMobileCard,
    CommonChip,
    OpenBalancesInvoiceTable
  },

  props: {
    value: {
      type: Object as PropType<ITableOptions>,
      required: true
    },
    items: {
      type: Array as PropType<IOpenBalanceListItem[]>,
      default: () => []
    },
    filterLoading: Boolean,
    isExportAllLoading: Boolean,
    exportLoadingKey: {
      type: String as PropType<Nullable<string>>,
      default: null
    }
  },

  data: (): IBrokerProductionTableData => ({
    headers,
    expanded: [],
  }),

  computed: {
    totals (): IBrokerProductionTableFooterTotals {
      const returnTotals = {
        count: this.items.length,
        expected: 0,
        inHouseExpected: 0,
        paid: 0,
        brokerGross: 0,
        remaining: 0,
        brokerGrossRemaining: 0
      }
      for (const item of this.items) {
        returnTotals.expected += item.expected;
        returnTotals.inHouseExpected += item.inHouseExpected;
        returnTotals.paid += item.payments;
        returnTotals.brokerGross += item.brokerGross;
        returnTotals.remaining += item.remaining;
        returnTotals.brokerGrossRemaining += item.brokerGrossRemaining;
      }
      return returnTotals
    }
  },

  methods: {
    updateModel (model: Partial<ITableOptions>) {
      this.$emit('input', { ...this.value, ...model });
    },
  }
});
</script>
